'use client';

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslations } from 'next-intl';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import {
  Info,
  FileText,
  FileImage,
  AlertTriangle,
  Loader2,
  Upload,
  PencilLine,
  HelpCircle,
} from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { Typography } from '@/shared/ui/shadcn/typography';
import { LoadingSpinner } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { useUploadStore } from '@/features/upload';
import { UpgradePlanText } from '@/shared/ui/UpgradePlanText';
import { usePlanTier } from '@/features/subscription';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { ControlEditorModal } from '@/features/control/ui/ControlEditorModal';
import { useProjectControls } from '@/features/control/hooks/use-project-controls';
import { controlsService } from '@/shared/api/services/controls.service';
import { FileTypes } from '@/shared/constants/file-types';
import { toast } from '@/shared/lib/toast';
import { useAnglesOverview } from '../hooks/use-angles-overview';
import { useProjectAnglesGuide } from '../hooks/use-angles-guide';
import {
  useUpdateProjectAngle,
  useDeleteProjectAngle,
  useReorderProjectAngles,
  useConfirmBuildingAngle,
  usePromoteUpload,
} from '@/shared/api/hooks/use-project-angles';
import { useProject } from '@/features/project/hooks/use-projects';
import { useUnfinishedIntakes } from '@/features/angle/ui/upload';
import { SectionAddButton } from '@/features/project';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { useConvertMaterialType } from '@/shared/api/hooks/use-material-convert';
import { ProjectAngleTile } from './ProjectAngleTile';
import { BriefTile, BriefTileGrid } from './BriefTile';
import { AngleEditDialog } from './AngleEditDialog';
import { AngleDeleteConflictDialog } from '@/shared/ui';
import { AnglesGuide } from './AnglesGuide';
import { BriefIntake, type BriefIntakeRef } from './BriefIntake';
import { ReferenceDetailDialog } from '@/features/project-reference/ui/ReferenceDetailDialog';
import {
  referenceCardType,
  useProjectReferencesPanel,
  type MaterialCardType,
} from '@/features/project-reference/hooks/use-project-references-panel';
import { useUpdateProjectReference } from '@/features/project-reference/hooks/use-project-references';
import { ApiClientError } from '@/shared/lib/api';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import {
  getProjectAngleValidationStatus,
  isProjectAngleValid,
  type AngleDeleteConflictMeta,
  type DeleteConflictStrategy,
  type ProjectAngle,
} from '@/types/project-angle';
import {
  hasImageFile,
  isImageReference,
  isRejectedReference,
  type ProjectReference,
} from '@/types/project-reference';
import type { Control } from '@/types/concept';

interface BriefMaterialsProps {
  projectId: string;
  isArchived?: boolean;
  // Called when the first photos land in a previously empty project, so the
  // page-level "Create design" flow can proactively open prefilled with them.
  onFirstUpload?: (angleIds: string[]) => void;
}

export interface BriefMaterialsRef {
  /** Feed dropped/picked files into the Brief intake (photos + documents). */
  processFiles: (files: File[]) => void;
}

// Sketch editor session: editing an existing control (with its angle backdrop)
// or creating a new one on a chosen angle. `control` seeds the editor (edit
// only); `angle` is always the backdrop photo.
interface SketchSession {
  // Binding angle (control.angle_id). null = free-standing brief sketch.
  angle: ProjectAngle | null;
  control: Control | null;
  // Currently selected backdrop angle id (control.backdrop_angle_id). null = no
  // backdrop (clean schema). Defaults to the binding angle, changeable in-editor.
  backdropAngleId: string | null;
}

/**
 * Brief "Your building" materials area — ONE unified surface for everything the
 * project starts from, identical across Web/Android/Apple. A header row with the
 * "Source materials" title + a "?" source-angles guide affordance on the left
 * and a single "+" add button on the right (no helper paragraph), then ONE
 * continuous square-tile grid below it. Every material flows into that single
 * grid in a fixed group order, left-to-right and top-to-bottom with no per-type
 * row break (cards from different groups share rows; the groups just stay
 * contiguous and ordered):
 *
 *  (a) Views    = angles (sortable render inputs; tap → editor)
 *  (b) Sketches = ALL project controls incl. unused drafts, newest first
 *                 (tap → ControlEditorModal seeded with the sketch + its angle)
 *  (c) Docs     = brief documents / brief-section references (tap → detail)
 *  (d) References = inspiration (style/material/colour; tap → detail)
 *
 * Every card reuses the SAME BriefTile so cards are square and the same size
 * for every type, each with a small type caption. BriefTileGrid derives the
 * column count from the container width (fixed tile size — never blown up when
 * there are only a few cards).
 *
 * The "+" opens a source menu: "Add files" (the mixed picker → photos to angle
 * validation, drawings/docs to project documents) and "Draw a sketch" (angle
 * picker if multiple views, else direct → ControlEditorModal in CREATE mode).
 */
export const BriefMaterials = forwardRef<BriefMaterialsRef, BriefMaterialsProps>(
  function BriefMaterials({ projectId, isArchived = false, onFirstUpload }, ref) {
  const t = useTranslations('angles');
  const tProject = useTranslations('project');
  const tUpload = useTranslations('angle.upload.modal');
  const tIntake = useTranslations('briefIntake');
  const tCommon = useTranslations('common');
  const tSketch = useTranslations('briefSketches');
  const tConvert = useTranslations('briefConvert');
  const tErrors = useTranslations('common.errors');
  const { isMax } = usePlanTier();
  const queryClient = useQueryClient();

  // ---- Photos (angles) ----------------------------------------------------
  const {
    angles,
    isLoading,
    error,
    angleCount,
    maxAngles,
    maxLibraryAngles,
    canAddMore,
    refetch,
  } = useAnglesOverview(projectId);

  const { data: anglesGuide } = useProjectAnglesGuide();
  const updateAngle = useUpdateProjectAngle(projectId);
  const deleteAngle = useDeleteProjectAngle(projectId);
  const reorderAngles = useReorderProjectAngles(projectId);

  // ---- Conversion rescue (view promotion) ----------------------------------
  // Server-computed `view_promotion`: the project has NO valid/pending view but
  // HAS a material that can be turned back into a view. Two server-picked
  // modes: 'promote' (auto-routed upload → confirm-building, same endpoint as
  // the upload flow's "Use as a view") and 'retype' (the user MANUALLY re-typed
  // their only view into a reference/doc → the existing reference→view
  // type-change chain, handleRetypeToView below). Without this banner the user
  // is stranded — Set up design stays disabled and nothing says the file's type
  // can be changed.
  const { data: projectResponse } = useProject(projectId);

  // L1: an intake still running when this page opened has nobody watching it —
  // the upload window it belonged to is gone with the tab.
  useUnfinishedIntakes(projectId);
  const viewPromotion = projectResponse?.data?.attributes?.view_promotion;
  const confirmBuilding = useConfirmBuildingAngle(projectId);
  const promoteUpload = usePromoteUpload(projectId);
  const handlePromoteToView = useCallback(() => {
    // The server names the id it wants promoted. An intake on the `/uploads`
    // contract created no angle at all, so `upload_id` is the only handle it
    // has; `angle_id` alone means the older angle contract ran this one.
    const onError = (err: unknown) => {
      toast.error(
        err instanceof ApiClientError ? err.getFirstError() : tErrors('generic')
      );
    };
    // Both hooks invalidate the project, angles and references caches — the
    // banner disappears and the view tile appears from those refetches.
    if (viewPromotion?.upload_id) {
      promoteUpload.mutate(viewPromotion.upload_id, { onError });

      return;
    }
    if (viewPromotion?.angle_id) {
      confirmBuilding.mutate(viewPromotion.angle_id, { onError });
    }
  }, [
    viewPromotion?.upload_id,
    viewPromotion?.angle_id,
    promoteUpload,
    confirmBuilding,
    tErrors,
  ]);

  // ---- Change material type (view <-> doc) --------------------------------
  // One shared mutation re-files a View as a Doc and a Doc as a View; on
  // success it refetches BOTH the views and the docs lists so the card hops
  // groups. Sketches are never convertible.
  const convertMaterial = useConvertMaterialType(projectId);
  // Follow-up PATCH for the View → Reference / Output style hops: the convert
  // crosses View → Doc and returns a NEW reference id, which we then re-tag.
  const updateReference = useUpdateProjectReference(projectId);

  // ---- Documents (brief references) ---------------------------------------
  const {
    userImageReferences,
    userDocumentReferences,
    deletingId,
    selectedReference,
    handleDelete: handleDeleteReference,
    handleOpenDetail,
    handleCloseDetail,
    handleSaveDetail,
    handleChangeType,
    isSavingDetail,
    isDeletingDetail,
    isMovingSection,
  } = useProjectReferencesPanel(projectId, {
    enablePolling: true,
    section: 'brief',
  });

  // ---- References (inspiration) -------------------------------------------
  // The standalone References page folded into the Brief grid (Phase 2). A
  // SECOND, section-scoped panel call powers the References block. Caches stay
  // section-scoped (no merging the two buckets) — `references.list(projectId,
  // 'reference')` is a distinct query key from the brief bucket above.
  const {
    userImageReferences: refImageReferences,
    userDocumentReferences: refDocumentReferences,
    deletingId: refDeletingId,
    selectedReference: selectedRef,
    handleDelete: handleDeleteRef,
    handleOpenDetail: handleOpenRefDetail,
    handleCloseDetail: handleCloseRefDetail,
    handleSaveDetail: handleSaveRefDetail,
    handleChangeType: handleChangeRefType,
    isSavingDetail: isSavingRefDetail,
    isDeletingDetail: isDeletingRefDetail,
    isMovingSection: isMovingRefSection,
  } = useProjectReferencesPanel(projectId, {
    enablePolling: true,
    section: 'reference',
  });

  // ---- Sketches (controls) ------------------------------------------------
  const { data: controls = [] } = useProjectControls(projectId);

  // ---- Unified intake (headless: file dialog + both upload pipelines) ------
  const intakeRef = useRef<BriefIntakeRef>(null);
  useImperativeHandle(
    ref,
    () => ({
      processFiles: (files: File[]) => intakeRef.current?.processFiles(files),
    }),
    []
  );

  // ---- Angle dialog state -------------------------------------------------
  const [editingAngle, setEditingAngle] = useState<ProjectAngle | null>(null);
  const [deletingAngleId, setDeletingAngleId] = useState<string | null>(null);
  const [angleToDelete, setAngleToDelete] = useState<ProjectAngle | null>(null);
  const [deleteConflict, setDeleteConflict] =
    useState<AngleDeleteConflictMeta | null>(null);
  // Server-localized conflict title (the 409 `detail`) — rendered verbatim as the
  // blocked-dialog heading for cross-platform parity with Apple/Android.
  const [deleteConflictDetail, setDeleteConflictDetail] = useState<string | null>(
    null
  );
  const [conflictAngleId, setConflictAngleId] = useState<string | null>(null);

  // ---- Sketch state -------------------------------------------------------
  const [sketchSession, setSketchSession] = useState<SketchSession | null>(null);
  const [sketchPickerOpen, setSketchPickerOpen] = useState(false);

  // Source picker — a centered modal (parity with Apple's ImageSourceSheet)
  // offering the single unified "Add files" intake and "Draw a sketch".
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);

  // Source-angles guide modal, opened from the "?" next to the section title.
  const [guideOpen, setGuideOpen] = useState(false);

  // Only valid (rendered) views can host a sketch backdrop.
  const usableAngles = useMemo(
    () => angles.filter((a) => isProjectAngleValid(a) && !!a.attributes.file_url),
    [angles]
  );
  // Resolve a control's backdrop photo from the already-loaded angles, by the
  // control's angle relationship (do not put the angle image on the control).
  const angleById = useMemo(() => {
    const map = new Map<string, ProjectAngle>();
    angles.forEach((a) => map.set(a.id, a));
    return map;
  }, [angles]);

  // ---- DnD ----------------------------------------------------------------
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = angles.findIndex((a) => a.id === active.id);
      const newIndex = angles.findIndex((a) => a.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        reorderAngles.mutate(arrayMove(angles, oldIndex, newIndex).map((a) => a.id));
      }
    },
    [angles, reorderAngles]
  );

  // ---- Draw a sketch (create flow) ----------------------------------------
  const handleStartSketch = useCallback(() => {
    const [first] = usableAngles;
    if (!first) {
      // No view yet → start a free-standing schema (no backdrop). Sketches are no
      // longer required to sit on a view; the user can draw a standalone diagram.
      setSketchSession({ angle: null, control: null, backdropAngleId: null });
      return;
    }
    if (usableAngles.length === 1) {
      setSketchSession({
        angle: first,
        control: null,
        backdropAngleId: first.id,
      });
    } else {
      setSketchPickerOpen(true);
    }
  }, [usableAngles]);

  const handlePickSketchAngle = useCallback((angle: ProjectAngle) => {
    setSketchPickerOpen(false);
    setSketchSession({ angle, control: null, backdropAngleId: angle.id });
  }, []);

  const handleOpenControl = useCallback(
    (control: Control) => {
      const angleId = control.relationships?.angle?.data?.id ?? null;
      const angle = angleId ? (angleById.get(angleId) ?? null) : null;
      // Backdrop defaults to the stored backdrop_angle_id, falling back to the
      // binding angle for legacy rows that predate the backdrop column.
      const backdropAngleId =
        control.attributes.backdrop_angle_id ?? angleId ?? null;
      setSketchSession({ angle, control, backdropAngleId });
    },
    [angleById]
  );

  const refreshControls = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.controls.listByProject(projectId),
    });
  }, [queryClient, projectId]);

  const handleSaveSketch = useCallback(
    async (blob: Blob) => {
      if (!sketchSession) return;
      try {
        if (sketchSession.control) {
          // Edit existing — API versions it if used in renders. Persist the
          // (possibly re-picked) backdrop.
          await controlsService.updateControl(
            sketchSession.control.id,
            FileTypes.CUSTOM,
            blob,
            { backdropAngleId: sketchSession.backdropAngleId }
          );
          toast.success(tSketch('toastSaved'));
        } else {
          // Brief sketches are free-standing (angle_id = null) on every platform:
          // the chosen view is only the backdrop, not a binding. backdrop_angle_id
          // = the chosen backdrop (null = clean schema).
          await controlsService.uploadControl(
            null,
            FileTypes.CUSTOM,
            blob,
            {
              projectId,
              backdropAngleId: sketchSession.backdropAngleId,
            }
          );
          toast.success(tSketch('toastCreated'));
        }
        setSketchSession(null);
        refreshControls();
      } catch (err) {
        // Surface server errors (e.g. the per-angle unused-sketch limit) as-is.
        toast.error(tSketch('toastSaveFailed'), {
          description:
            err instanceof ApiClientError
              ? err.getFirstError()
              : tErrors('generic'),
        });
        // Keep the editor open so the user can retry.
      }
    },
    [sketchSession, projectId, tSketch, tErrors, refreshControls]
  );

  // Backdrop picker options: "No backdrop" + every usable (rendered) view.
  const backdropOptions = useMemo(
    () => [
      { id: null, url: null, label: tSketch('backdropNone') },
      ...usableAngles.map((a) => ({
        id: a.id,
        url: a.attributes.file_url ?? null,
        label: a.attributes.name || tSketch('backdropView'),
      })),
    ],
    [usableAngles, tSketch]
  );

  // Resolve the selected backdrop's photo URL for the editor overlay.
  const sketchBackdropUrl = useMemo(() => {
    if (!sketchSession?.backdropAngleId) return null;
    return angleById.get(sketchSession.backdropAngleId)?.attributes.file_url ?? null;
  }, [sketchSession?.backdropAngleId, angleById]);

  // ---- Angle edit / delete ------------------------------------------------
  const handleSaveEdit = useCallback(
    async (description: string) => {
      if (!editingAngle) return;
      await updateAngle.mutateAsync({
        angleId: editingAngle.id,
        updates: { description: description || null },
      });
      setEditingAngle(null);
    },
    [editingAngle, updateAngle]
  );

  // Re-file the open view as a project document. Closes the editor on success;
  // surfaces the server's verbatim rejection (e.g. "view has renders") on 4xx.
  // Change a View's (angle's) type from the angle edit dialog. Mirrors the
  // reference panel's handleChangeType but the source is an angle, so every
  // non-Document target routes through the convert hub: View → Doc is a single
  // materials/convert; View → Reference / Output style convert to a Doc first,
  // then PATCH the NEW reference (the convert returns a fresh id) to the target
  // state. Server 4xx (e.g. "view has renders") surfaces verbatim — no gating.
  const handleChangeAngleType = useCallback(
    async (target: MaterialCardType) => {
      if (!editingAngle || target === 'view') return;
      try {
        const created = await convertMaterial.mutateAsync({
          source_type: 'view',
          source_id: editingAngle.id,
          target_type: 'doc',
        });
        if (target === 'reference') {
          await updateReference.mutateAsync({
            referenceId: created.id,
            section: 'reference',
            aspects: ['style'],
          });
        } else if (target === 'output') {
          // Server clears aspects when category is set to output.
          await updateReference.mutateAsync({
            referenceId: created.id,
            section: 'reference',
            category: 'output',
          });
        }
        setEditingAngle(null);
        refetch();
        // Only the plain View → Doc hop gets a confirmation toast (matching the
        // reference panel, which re-types silently); the chained hops would read
        // misleadingly as "converted to document".
        if (target === 'document') toast.success(tConvert('toastToDoc'));
      } catch (err) {
        toast.error(tConvert('toastFailed'), {
          description:
            err instanceof ApiClientError ? err.getFirstError() : tErrors('generic'),
        });
      }
    },
    [editingAngle, convertMaterial, updateReference, refetch, tConvert, tErrors]
  );

  // Re-file a brief document (by id) as a view (photo) via materials/convert.
  // Used as the View hop of the unified type selector — the panel's
  // handleChangeType already PATCHes a Reference/Output down to a brief Document
  // first (convert only accepts a Document source), then calls this. On success
  // refreshes the views list + toasts; on a 4xx it surfaces the server's
  // verbatim rejection (e.g. "view has renders") and RE-THROWS so the panel
  // keeps the dialog open instead of closing on a failed convert.
  const convertDocIdToView = useCallback(
    async (referenceId: string) => {
      try {
        await convertMaterial.mutateAsync({
          source_type: 'doc',
          source_id: referenceId,
          target_type: 'view',
        });
        refetch();
        toast.success(tConvert('toastToView'));
      } catch (err) {
        toast.error(tConvert('toastFailed'), {
          description:
            err instanceof ApiClientError
              ? err.getFirstError()
              : tErrors('generic'),
        });
        throw err;
      }
    },
    [convertMaterial, refetch, tConvert, tErrors]
  );

  // Conversion rescue, 'retype' mode: `angle_id` is null (there is no soft
  // -deleted angle to confirm), `reference_id` points at the row the user
  // manually re-typed away from being a view. The rescue is the SAME
  // reference→view chain as the detail dialog's View hop
  // (useProjectReferencesPanel.handleChangeType): a brief-section Document
  // converts directly (materials/convert doc→view); a reference-section row is
  // first re-filed to a brief Document (convert only accepts a Document
  // source), then converted. useConvertMaterialType's onSuccess invalidates
  // the project + angles + references caches, so the banner clears and the
  // view tile appears exactly like the promote path.
  const handleRetypeToView = useCallback(async () => {
    const referenceId = viewPromotion?.reference_id;
    if (!referenceId) return;
    // Resolve the row's section from the loaded brief bucket: found there =
    // already a Document, convert directly. Anything else (reference/output
    // section — or a row the brief query hasn't loaded, where the PATCH is an
    // idempotent no-op for a brief doc) gets the PATCH-to-brief hop first.
    const isBriefDoc =
      userImageReferences.some((r) => r.id === referenceId) ||
      userDocumentReferences.some((r) => r.id === referenceId);
    if (!isBriefDoc) {
      try {
        await updateReference.mutateAsync({ referenceId, section: 'brief' });
      } catch (err) {
        toast.error(tConvert('toastFailed'), {
          description:
            err instanceof ApiClientError
              ? err.getFirstError()
              : tErrors('generic'),
        });
        return;
      }
    }
    // convertDocIdToView already toasts its own failure — swallow the re-throw.
    await convertDocIdToView(referenceId).catch(() => {});
  }, [
    viewPromotion?.reference_id,
    userImageReferences,
    userDocumentReferences,
    updateReference,
    convertDocIdToView,
    tConvert,
    tErrors,
  ]);

  const handleDeleteClick = useCallback((angle: ProjectAngle) => {
    const status = getProjectAngleValidationStatus(angle);
    if (status === 'pending' || status === 'processing') return;
    setAngleToDelete(angle);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!angleToDelete) return;
    const angleId = angleToDelete.id;
    setDeletingAngleId(angleId);
    setAngleToDelete(null);
    try {
      await deleteAngle.mutateAsync({ angleId });
    } catch (err) {
      if (err instanceof ApiClientError && err.isAngleDeleteConflictError()) {
        const meta = err.getAngleDeleteConflict();
        if (meta) {
          setDeleteConflict(meta);
          setDeleteConflictDetail(err.message);
          setConflictAngleId(angleId);
        }
      } else {
        console.error('Delete failed:', err);
      }
    } finally {
      setDeletingAngleId(null);
    }
  }, [angleToDelete, deleteAngle]);

  const handleConflictChoose = useCallback(
    async (strategy: DeleteConflictStrategy) => {
      if (!conflictAngleId) return;
      const angleId = conflictAngleId;
      setDeletingAngleId(angleId);
      try {
        await deleteAngle.mutateAsync({ angleId, strategy });
      } catch (err) {
        console.error('Delete failed:', err);
      } finally {
        setDeletingAngleId(null);
        setDeleteConflict(null);
        setDeleteConflictDetail(null);
        setConflictAngleId(null);
      }
    },
    [conflictAngleId, deleteAngle]
  );

  const handleConflictCancel = useCallback(() => {
    setDeleteConflict(null);
    setDeleteConflictDetail(null);
    setConflictAngleId(null);
  }, []);

  const briefDocuments = [...userImageReferences, ...userDocumentReferences];
  const references = [...refImageReferences, ...refDocumentReferences];

  // In-flight document uploads (PDF/txt/md) from the shared upload store — both
  // the single-file (queue) and multi-file (batch) paths. Rendered as optimistic
  // square placeholders inside the Docs grid so there is only ever ONE card per
  // document, in the right place, from the moment it is picked. Filtering by
  // active status means a completed/failed upload drops out automatically (the
  // real, still-processing tile then takes over).
  const uploadQueue = useUploadStore((s) => s.queue);
  const uploadBatches = useUploadStore((s) => s.batches);
  const pendingDocUploads = useMemo(() => {
    const all = [
      ...uploadQueue,
      ...Object.values(uploadBatches).flatMap((b) => b.items),
    ];
    return all.filter(
      (it) =>
        it.type === 'reference' &&
        !it.file.type.startsWith('image/') &&
        (it.status === 'pending' ||
          it.status === 'processing' ||
          it.status === 'uploading')
    );
  }, [uploadQueue, uploadBatches]);

  // Fully empty: no views, no documents (persisted or in-flight), no sketches.
  // Drives a References-style placeholder (with its own uploader) AND hides the
  // header "+" so there is only ever ONE upload affordance in the empty state.
  const isEmpty =
    angles.length === 0 &&
    briefDocuments.length === 0 &&
    pendingDocUploads.length === 0 &&
    controls.length === 0 &&
    references.length === 0;

  return (
    <section className="space-y-4">
      {/* "Source materials" block title (pronounced subheading, matching the
          "Situation and goal" brief heading) on the left; the "+" on the right
          opens a source menu: add files (mixed intake) or draw a sketch (angle
          picker if needed → ControlEditorModal in CREATE mode). Identical
          across Web/Android/Apple. */}
      <div className="flex items-center justify-between gap-2 pb-1">
        <div className="flex items-center gap-2">
          <span className="text-card-title text-foreground">
            {tProject('buildingMaterials')}
          </span>
          {anglesGuide && (
            <button
              type="button"
              onClick={() => setGuideOpen(true)}
              aria-label={anglesGuide.trigger}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          )}
        </div>
        {/* Header "+" is hidden in the empty state — the placeholder below owns
            the single uploader there, so two upload buttons never show at once. */}
        {!isArchived && !isEmpty && (
          <SectionAddButton
            ariaLabel={tIntake('addFilesHeader')}
            onClick={() => setSourcePickerOpen(true)}
          />
        )}
      </div>

      {/* Headless intake: mixed file dialog + image validation pipeline +
          Brief-document upload. Fed by the source menu, the empty-state button
          and the page-wide drop target (via the forwarded ref). */}
      <BriefIntake
        ref={intakeRef}
        projectId={projectId}
        onFirstUpload={onFirstUpload}
      />

      {/* Conversion rescue — the project has no view. Same banner text and
          button for both server-picked modes: 'promote' (upload auto-routed
          into References → the upload flow's own "Use as a view" action) and 'retype' (the user manually
          re-typed their only view → the existing reference→view type-change
          chain). Server-driven flag; the banner clears via the
          project/angles/references invalidations on success. */}
      {!isArchived &&
        viewPromotion?.is_available &&
        (viewPromotion.mode === 'retype'
          ? !!viewPromotion.reference_id
          : !!viewPromotion.upload_id || !!viewPromotion.angle_id) && (
          <NoticeBanner
            variant="info"
            message={t('viewPromotionBanner')}
            action={
              <Button
                size="sm"
                variant="outline"
                onClick={
                  viewPromotion.mode === 'retype'
                    ? handleRetypeToView
                    : handlePromoteToView
                }
                disabled={
                  viewPromotion.mode === 'retype'
                    ? updateReference.isPending || convertMaterial.isPending
                    : promoteUpload.isPending || confirmBuilding.isPending
                }
              >
                {tUpload('useAsView')}
              </Button>
            }
          />
        )}

      {/* Loading / error / stacked material blocks */}
      {isLoading && angles.length === 0 ? (
        <LoadingSpinner size="lg" className="py-12" />
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription>{error.message || t('loadError')}</AlertDescription>
        </Alert>
      ) : isEmpty && !isArchived ? (
        /* Fully empty: a References-style placeholder (muted icon + title +
           description + a prominent uploader) that owns the SINGLE add
           affordance here. Inline and compact — small vertical padding on the
           single-column (narrow) layout (overriding EmptyState's default py-16)
           so the "Situation and goal" section below stays visible without
           scrolling; roomier only at lg+ (two-column). */
        <EmptyState
          icon={
            <div className="flex items-center gap-3">
              <FileImage className="h-12 w-12" />
              <FileText className="h-12 w-12" />
            </div>
          }
          title={tIntake('empty.title')}
          description={tIntake('empty.description')}
          action={{
            label: tIntake('addFiles'),
            onClick: () => intakeRef.current?.openFileDialog(),
          }}
          className="!py-8 lg:!py-16"
        />
      ) : (
        <>
          {/* ONE continuous grid for every material, in a fixed group order —
              Views → Sketches → Documents → References — flowing left-to-right,
              top-to-bottom with NO per-type row break (cards from different
              groups share a row, the groups just stay contiguous and ordered).
              All cards reuse BriefTile so they are the same square size; the
              column count is count- and width-aware (BriefTileGrid). The whole
              grid is wrapped in ONE DndContext so the Views (angles) stay
              reorderable — SortableContext registers only the angle ids, so the
              other cards flow in the same grid without participating in drag. */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={angles.map((a) => a.id)}
              strategy={rectSortingStrategy}
            >
              <BriefTileGrid>
                {/* (a) Views — sortable angles. */}
                {angles.map((angle, index) => (
                  <ProjectAngleTile
                    key={angle.id}
                    angle={angle}
                    index={index}
                    onEdit={() => setEditingAngle(angle)}
                    isDeleting={deletingAngleId === angle.id}
                  />
                ))}

                {/* (b) Sketches — ALL project controls, incl. unused drafts. */}
                {controls.map((control) => {
                  const angleId = control.relationships?.angle?.data?.id;
                  const backdropUrl = angleId
                    ? angleById.get(angleId)?.attributes.file_url ?? undefined
                    : undefined;
                  return (
                    <BriefSketchTile
                      key={control.id}
                      control={control}
                      backdropUrl={backdropUrl}
                      onOpen={() => handleOpenControl(control)}
                    />
                  );
                })}

                {/* (c) Documents — brief-section documents. In-flight uploads
                    lead the documents group as optimistic placeholders, then
                    drop out as the real, still-processing tile takes over. */}
                {pendingDocUploads.map((it) => (
                  <BriefDocPlaceholderTile key={it.id} fileName={it.file.name} />
                ))}
                {briefDocuments.map((reference) => (
                  <BriefDocTile
                    key={reference.id}
                    reference={reference}
                    onOpen={() => handleOpenDetail(reference)}
                    isDeleting={deletingId === reference.id}
                  />
                ))}

                {/* (d) References — inspiration (style/material/colour). Same
                    tile as a Document, just a different caption; tap → the same
                    detail dialog (Reference cards can re-type to Document). */}
                {references.map((reference, index) => (
                  <BriefReferenceTile
                    key={reference.id}
                    reference={reference}
                    index={index}
                    onOpen={() => handleOpenRefDetail(reference)}
                    isDeleting={refDeletingId === reference.id}
                  />
                ))}
              </BriefTileGrid>
            </SortableContext>
          </DndContext>

          {/* Project library usage — only when the library is full. */}
          {!canAddMore && (
            <div className="flex flex-col gap-1 text-muted-foreground/60">
              <div className="flex items-center gap-2 whitespace-nowrap">
                <Info className="h-4 w-4 shrink-0" />
                <Typography variant="small">
                  {t('libraryUsed', {
                    count: angleCount,
                    limit: maxLibraryAngles,
                  })}
                </Typography>
                {!isMax && <UpgradePlanText />}
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap ps-6">
                <Typography variant="small">
                  {t('anglesPerConcept', { limit: maxAngles })}
                </Typography>
              </div>
            </div>
          )}
        </>
      )}

      {/* Source-angles guide — opened from the "?" next to the section title. */}
      {anglesGuide && (
        <AnglesGuide
          guide={anglesGuide}
          open={guideOpen}
          onOpenChange={setGuideOpen}
        />
      )}

      {/* Source picker — a centered modal (parity with Apple's ImageSourceSheet),
          replacing the corner dropdown. Two sources only: the unified "Add files"
          intake (photos → views, drawings/docs → project documents) and "Draw a
          sketch". The legacy "Add reference" path is intentionally gone — every
          file now flows through the single "Add files" window. */}
      <Dialog open={sourcePickerOpen} onOpenChange={setSourcePickerOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{tIntake('addFilesHeader')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                setSourcePickerOpen(false);
                intakeRef.current?.openFileDialog();
              }}
              className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-start transition-colors hover:bg-accent"
            >
              <Upload className="h-5 w-5 shrink-0 text-muted-foreground" />
              <span className="text-list-item-title text-foreground">
                {tIntake('addFiles')}
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setSourcePickerOpen(false);
                handleStartSketch();
              }}
              className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-start transition-colors hover:bg-accent"
            >
              <PencilLine className="h-5 w-5 shrink-0 text-muted-foreground" />
              <span className="text-list-item-title text-foreground">
                {tSketch('createNew')}
              </span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sketch angle picker — only when the project has multiple views. */}
      <Dialog open={sketchPickerOpen} onOpenChange={setSketchPickerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tSketch('pickAngleTitle')}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {usableAngles.map((angle) => (
              <button
                key={angle.id}
                type="button"
                onClick={() => handlePickSketchAngle(angle)}
                aria-label={
                  angle.attributes.name ||
                  angle.attributes.original_filename ||
                  tSketch('pickAngleTitle')
                }
                className="relative aspect-square w-full overflow-hidden rounded-lg border border-border bg-muted shadow-sm transition-shadow hover:shadow-md"
              >
                {angle.attributes.file_url && (
                  <img
                    src={angle.attributes.file_url}
                    alt={angle.attributes.name || ''}
                    className="h-full w-full object-cover"
                  />
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Full-screen sketch editor — same modal as render settings, seeded with
          the chosen sketch (edit) or empty (create) over its angle backdrop. */}
      {sketchSession && (
        <ControlEditorModal
          isOpen={!!sketchSession}
          angleImageUrl={sketchBackdropUrl}
          initialControlUrl={sketchSession.control?.attributes.file?.url}
          onSave={handleSaveSketch}
          onCancel={() => setSketchSession(null)}
          backdropOptions={backdropOptions}
          selectedBackdropAngleId={sketchSession.backdropAngleId}
          onBackdropChange={(angleId) =>
            setSketchSession((prev) =>
              prev ? { ...prev, backdropAngleId: angleId } : prev
            )
          }
        />
      )}

      {/* Angle editor */}
      <AngleEditDialog
        open={!!editingAngle}
        onOpenChange={(open) => !open && setEditingAngle(null)}
        angle={editingAngle}
        description={editingAngle?.attributes.description || ''}
        onSave={handleSaveEdit}
        onDelete={
          editingAngle
            ? () => {
                const angle = editingAngle;
                setEditingAngle(null);
                handleDeleteClick(angle);
              }
            : undefined
        }
        onChangeType={!isArchived ? handleChangeAngleType : undefined}
        isConverting={convertMaterial.isPending || updateReference.isPending}
        isSaving={updateAngle.isPending}
      />

      {/* Angle delete confirm */}
      <AlertDialog
        open={!!angleToDelete}
        onOpenChange={(open) => !open && setAngleToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirmMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* Always the simple confirmation. When the view is the main view of
              other designs the server BLOCKS the delete (409, is_blocked) and
              the conflict dialog explains it — we no longer promise to remove
              designs client-side, which was a false promise (the delete failed). */}
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Angle delete conflict (renders/main of other concepts → 409) */}
      <AngleDeleteConflictDialog
        conflict={deleteConflict}
        detail={deleteConflictDetail}
        isPending={!!deletingAngleId}
        onChoose={handleConflictChoose}
        onCancel={handleConflictCancel}
      />

      {/* Document detail — Brief-section item. Unified type selector offers all
          four types (View · Document · Reference · Output style); the panel's
          handleChangeType runs the right hop, converting to a View via
          convertDocIdToView (the open item is already a brief Document). */}
      <ReferenceDetailDialog
        reference={selectedReference}
        open={!!selectedReference}
        onClose={handleCloseDetail}
        onSave={handleSaveDetail}
        onDelete={(referenceId) => handleDeleteReference(referenceId)}
        onChangeType={(target) =>
          handleChangeType(
            target,
            selectedReference
              ? () => convertDocIdToView(selectedReference.id)
              : undefined
          )
        }
        canConvertToView={!isArchived}
        isConverting={convertMaterial.isPending}
        isSaving={isSavingDetail}
        isDeleting={isDeletingDetail}
        isMoving={isMovingSection}
      />

      {/* Reference detail — inspiration item. Same four-type selector; for the
          View hop the panel re-files the Reference/Output down to a brief
          Document first, then convertDocIdToView turns it into a View. */}
      <ReferenceDetailDialog
        reference={selectedRef}
        open={!!selectedRef}
        onClose={handleCloseRefDetail}
        onSave={handleSaveRefDetail}
        onDelete={(referenceId) => handleDeleteRef(referenceId)}
        onChangeType={(target) =>
          handleChangeRefType(
            target,
            selectedRef
              ? () => convertDocIdToView(selectedRef.id)
              : undefined
          )
        }
        canConvertToView={!isArchived}
        isConverting={convertMaterial.isPending}
        isSaving={isSavingRefDetail}
        isDeleting={isDeletingRefDetail}
        isMoving={isMovingRefSection}
      />
    </section>
  );
  }
);

interface BriefDocTileProps {
  reference: ProjectReference;
  onOpen: () => void;
  isDeleting?: boolean;
}

/**
 * Square tile for a current-state document ("Doc") in the unified Brief grid.
 * Image documents show a thumbnail; file documents (PDF/txt/md) show a file
 * icon + name. Tap opens the detail dialog (edit / move / delete). Renders
 * through the shared BriefTile so sizing + the type caption match Views/Sketches.
 */
function BriefDocTile({ reference, onOpen, isDeleting = false }: BriefDocTileProps) {
  const t = useTranslations('projectReference');
  const tCaption = useTranslations('briefSketches');
  // Show the actual picture for any image-backed material — including a document
  // (category 'data') that is really an image, like a satellite/cadastral map.
  const isImage = hasImageFile(reference) || isImageReference(reference);
  const isRejected = isRejectedReference(reference);
  const status = reference.attributes.classification_status;
  const isPending = status === 'pending' || status === 'processing';
  const isFailed = status === 'failed';
  const fileName =
    reference.attributes.metadata?.extraction?.document_title ||
    reference.attributes.metadata?.original_filename ||
    t('panel.documents');
  const imageUrl = reference.attributes.file_url;
  // Once the extractor has typed the sheet, the caption becomes the ROLE badge
  // ("Floor plan · floor 2") instead of the generic "Doc" — that badge is what
  // tells the user we actually read the drawing. Server-composed and localized;
  // shown verbatim, never assembled here. Untyped documents keep "Doc".
  const caption = reference.attributes.document_badge || tCaption('caption.doc');

  return (
    <BriefTile
      onClick={onOpen}
      ariaLabel={fileName}
      caption={caption}
      isDeleting={isDeleting}
    >
      {isImage && imageUrl && !isRejected ? (
        <img src={imageUrl} alt={fileName} className="w-full h-full object-cover" />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-muted-foreground">
          <FileText className="h-14 w-14" />
          <span className="text-card-meta line-clamp-2 text-center">
            {fileName}
          </span>
        </div>
      )}

      {isPending && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-background/70 p-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-card-meta text-muted-foreground text-center">
            {t('status.processingHint')}
          </span>
        </div>
      )}
      {(isFailed || isRejected) && (
        <div className="absolute inset-0 flex items-center justify-center bg-destructive/30">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
      )}
    </BriefTile>
  );
}

interface BriefReferenceTileProps extends BriefDocTileProps {
  /** Position within the references list — drives the alternating paper tilt. */
  index: number;
}

/**
 * Square tile for an inspiration reference ("Reference") in the unified Brief
 * grid. Visually a BriefDocTile (image thumbnail or file icon + name) with a
 * "Reference" caption instead of "Doc". Tap opens the same detail dialog (where
 * a Reference can re-type to Document). Kept separate from BriefDocTile only for
 * the caption + future reference-specific affordances; otherwise identical.
 *
 * IMAGE reference tiles get the alternating "paper tilt" (+2.5° / -2.5° by
 * index) + paper shadow — unified across web/Android/Apple (Apple's
 * paperCardStyleCompensated is the reference look). File/document reference
 * tiles stay straight.
 */
function BriefReferenceTile({
  reference,
  index,
  onOpen,
  isDeleting = false,
}: BriefReferenceTileProps) {
  const t = useTranslations('projectReference');
  const tCaption = useTranslations('briefSketches');
  // Show the actual picture for any image-backed material — including a document
  // (category 'data') that is really an image, like a satellite/cadastral map.
  const isImage = hasImageFile(reference) || isImageReference(reference);
  const isRejected = isRejectedReference(reference);
  const status = reference.attributes.classification_status;
  const isPending = status === 'pending' || status === 'processing';
  const isFailed = status === 'failed';
  const fileName =
    reference.attributes.metadata?.extraction?.document_title ||
    reference.attributes.metadata?.original_filename ||
    t('section.title');
  const imageUrl = reference.attributes.file_url;
  // Tilt only the photo (image) cards, leaving file/document tiles straight.
  const rotation = isImage ? (index % 2 === 0 ? 2.5 : -2.5) : undefined;
  // An output-style reference (category=output) lives in the References section
  // but is a distinct entity to the user — caption it "Output style", not
  // "Reference". Reuse referenceCardType (same mapping the detail type selector
  // uses) so the outer tile and the inner type switcher never disagree.
  const caption =
    referenceCardType(reference) === 'output'
      ? t('category.output')
      : tCaption('caption.reference');

  return (
    <BriefTile
      onClick={onOpen}
      ariaLabel={fileName}
      caption={caption}
      isDeleting={isDeleting}
      rotation={rotation}
    >
      {isImage && imageUrl && !isRejected ? (
        <img src={imageUrl} alt={fileName} className="w-full h-full object-cover" />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-muted-foreground">
          <FileText className="h-14 w-14" />
          <span className="text-card-meta line-clamp-2 text-center">
            {fileName}
          </span>
        </div>
      )}

      {isPending && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-background/70 p-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-card-meta text-muted-foreground text-center">
            {t('status.processingHint')}
          </span>
        </div>
      )}
      {(isFailed || isRejected) && (
        <div className="absolute inset-0 flex items-center justify-center bg-destructive/30">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
      )}
    </BriefTile>
  );
}

/**
 * Optimistic square placeholder for an in-flight document upload (PDF/txt/md).
 * Visually identical to a real file-document tile (file icon + name) with the
 * processing overlay on top, so the hand-off to the persisted, still-processing
 * tile is seamless. Not clickable — it has no reference to open yet.
 */
function BriefDocPlaceholderTile({ fileName }: { fileName: string }) {
  const t = useTranslations('projectReference');
  const tCaption = useTranslations('briefSketches');

  return (
    <BriefTile ariaLabel={fileName} caption={tCaption('caption.doc')}>
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-muted-foreground">
        <FileText className="h-14 w-14" />
        <span className="text-card-meta line-clamp-2 text-center">
          {fileName}
        </span>
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-background/70 p-2">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="text-card-meta text-muted-foreground text-center">
          {t('status.processingHint')}
        </span>
      </div>
    </BriefTile>
  );
}

interface BriefSketchTileProps {
  control: Control;
  /** Backdrop photo (the control's angle), drawn faintly under the strokes. */
  backdropUrl?: string;
  onOpen: () => void;
}

/**
 * Square tile for a sketch ("Sketch") — a project control, incl. unused drafts.
 * Tap opens the ControlEditorModal seeded with the sketch + its angle backdrop.
 * Renders through the shared BriefTile so sizing + the type caption match
 * Views/Docs.
 */
function BriefSketchTile({ control, backdropUrl, onOpen }: BriefSketchTileProps) {
  const t = useTranslations('briefSketches');
  const url = control.attributes.file?.url;
  return (
    <BriefTile
      onClick={onOpen}
      ariaLabel={t('openSketch')}
      caption={t('caption.sketch')}
    >
      {/* Flat surface under the stroke overlay (parity with the render-settings
          control card). Black in dark mode keeps a poorly-transparent/light
          backdrop photo dark instead of glowing as a bright white block. */}
      <div className="absolute inset-0 bg-white dark:bg-black" />
      {/* Desaturated backdrop (the sketch's angle photo) under the strokes, so
          the transparent PNG reads against context — parity with the picker. */}
      {backdropUrl && (
        <img
          src={backdropUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-30 saturate-[0.25] brightness-95"
        />
      )}
      {url && (
        <img
          src={url}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
    </BriefTile>
  );
}
